import axios from "axios";
import { useRef, useState, useEffect } from "react";

import { gGateway } from "@utils/gateway";

import { useReRender } from "./re-render";

export type AudioManager = {
  resume(): Promise<void>;
  pause(): void;
  seek(progress: number): void;
  isLoading: boolean;
  isPlaying: boolean;
  didError: boolean;
  timeElapsedMs: number | undefined;
  durationMs: number | undefined;
};

/**
 * begin playback of the specified url and return controls to render an audio
 * player interface.
 */
export function useAudio({
  src,
  onEnd,
  notifier,
  autoplay,
  playbackRate,
}: {
  src: string | undefined;
  onEnd?(): void;
  autoplay?: boolean;
  playbackRate?: number;
  notifier: {
    notify: (options: {
      id: string;
      body: string;
      type: "success" | "error";
    }) => void;
  };
}): AudioManager {
  const forceUpdate = useReRender();

  const audioRef = useRef<HTMLAudioElement | undefined>(undefined);
  const durationMsRef = useRef<number | undefined>(undefined);
  const [didError, setDidError] = useState(false);
  const [isLoading, setLoading] = useState(autoplay ?? false);
  const [isPlaying, setPlaying] = useState(autoplay ?? false);
  const [timeElapsedMs, setTimeElapsedMs] = useState<number | undefined>(
    undefined,
  );
  const resolvedSrcPromiseRef = useRef<Promise<void>>(undefined);

  useEffect(
    () => {
      if (!src) {
        return;
      }

      let isActiveSrc = true;

      setDidError(false);
      const audio = document.createElement("audio");
      audio.preload = "metadata";
      audio.playbackRate = playbackRate ?? 1;
      document.body.appendChild(audio);

      // cache reference to the playback element, so we can reference it later
      // on in the control methods that are exposed publicly
      audioRef.current = audio;

      resolvedSrcPromiseRef.current = new Promise<void>((resolve) => {
        getAudioUrlFromSrc(src)
          .then((url) => {
            if (!isActiveSrc) {
              // bail out if the active audio src has been changed since we
              // requested the absolute url
              return;
            }

            if (!url) {
              setDidError(true);
              return;
            }

            audio.src = url;
            audio.autoplay = autoplay ?? false;

            // begin playback instantly if autoplay is set
            if (autoplay) {
              audio
                .play()
                .catch((err) => {
                  notifier.notify({
                    id: "audio.play.error",
                    body: "Der opstod en fejl ved afspilningen af lydfilen.",
                    type: "error",
                  });
                  console.error(err);
                })
                .finally(() => {
                  setLoading(false);
                });
            }

            // reset internal state so we reflect that a new file is currently
            // being played
            durationMsRef.current = undefined;
            setTimeElapsedMs(undefined);

            if (autoplay) {
              setPlaying(true);
            }
          })
          .catch((err) => {
            console.warn(err);
            setDidError(true);
          })
          .finally(() => resolve());
      });

      return (): void => {
        // register that this src is no longer actively requested
        isActiveSrc = false;

        // stop playback when the component is unmounted
        audio.pause();
        audio.remove();
      };
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [src],
  );

  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    let animationFrame: number;

    // subscribe to progress using an animation frame loop while the audio
    // is still playing
    const onProgress = (): void => {
      if (audioRef.current?.duration) {
        durationMsRef.current = audioRef.current?.duration * 1000;
      }

      if (audioRef.current?.currentTime) {
        setTimeElapsedMs(audioRef.current?.currentTime * 1000);
      }

      animationFrame = requestAnimationFrame(onProgress);
    };

    animationFrame = requestAnimationFrame(onProgress);

    return () => {
      // stop tracking progress once the component has been unmounted
      cancelAnimationFrame(animationFrame);
    };
  }, [isPlaying]);

  // bind event listeners to ensure that audio progress is going to be emitted
  // properly
  useEffect(() => {
    const audio = audioRef.current;

    const onLoaded = () => {
      if (audio) {
        durationMsRef.current = audio.duration * 1000;
        forceUpdate();
      }
    };

    const onError = () => {
      setPlaying(false);
      setTimeElapsedMs(0);
    };

    const onEnded = () => {
      setPlaying(false);
      setTimeElapsedMs(0);

      if (audio) {
        audio.currentTime = 0;
      }

      if (onEnd) {
        onEnd();
      }
    };

    const onPause = () => {
      setPlaying(false);
    };

    audio?.addEventListener("loadedmetadata", onLoaded);
    audio?.addEventListener("pause", onPause);
    audio?.addEventListener("error", onError);
    audio?.addEventListener("ended", onEnded);

    return () => {
      audio?.removeEventListener("loadedmetadata", onLoaded);
      audio?.removeEventListener("pause", onPause);
      audio?.removeEventListener("error", onError);
      audio?.removeEventListener("ended", onEnded);
    };
  }, [src, onEnd, forceUpdate]);

  useEffect(() => {
    const audio = audioRef.current;

    if (audio) {
      audio.playbackRate = playbackRate ?? 1;
    }
  }, [playbackRate]);

  return {
    async resume(): Promise<void> {
      setLoading(true);
      setPlaying(true);

      try {
        await resolvedSrcPromiseRef.current;

        if (!audioRef.current?.src) {
          throw new Error(
            "useAudio.resume(): Failed to play audio, src was not resolved properly",
          );
        }

        await audioRef.current?.play();

        // Stop playback from all other possibly active media to avoid
        // multiple sources playing at the same time.
        for (const mediaPlayer of Array.from(
          document.body.querySelectorAll<HTMLAudioElement | HTMLVideoElement>(
            "audio, video",
          ),
        )) {
          if (
            mediaPlayer.src !== audioRef.current?.src &&
            !mediaPlayer.paused
          ) {
            mediaPlayer.pause();
          }
        }
      } catch (err) {
        console.error(err);
        notifier.notify({
          id: "audio.error",
          type: "error",
          body: "Noget gik galt da vi forsøgte at afspille lyden. Prøv igen senere.",
        });

        setPlaying(false);
        setTimeElapsedMs(0);

        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
      } finally {
        setLoading(false);
      }
    },

    pause(): void {
      audioRef.current?.pause();
      setPlaying(false);
      setLoading(false);
    },

    seek(timeMs: number): void {
      if (audioRef.current) {
        audioRef.current.currentTime = timeMs / 1000;
        setTimeElapsedMs(timeMs);
      }
    },

    didError,
    isLoading,
    isPlaying,
    durationMs: durationMsRef.current,
    timeElapsedMs: timeElapsedMs,
  };
}

type MediaLibraryItem = {
  Mp3Url?: string;
  Mp4Url?: string;
  OggUrl?: string;
  WebMUrl?: string;
};

type MediaLibraryResponse = MediaLibraryItem | MediaLibraryItem[] | undefined;

const urlCache = new Map<string, string>();

/**
 * resolve absolute url for media files stored in Gyldendals MediaLibrary
 */
export async function getAudioUrlFromSrc(
  src: string | undefined,
): Promise<string | undefined> {
  if (!src) {
    return undefined;
  }

  const cachedUrl = urlCache.get(src);

  if (cachedUrl) {
    return cachedUrl;
  }

  if (src.includes("://")) {
    // return as is if we were already given an absolute URL
    return src;
  }

  const response = await axios.get<MediaLibraryResponse>(
    gGateway(`${urls.mediaLibrary}/api/mediafiles/${src}`),
  );

  if (!response.data) {
    return;
  }

  if (Array.isArray(response.data)) {
    const url = response.data.map(getMp3Url).find((it) => it !== undefined);

    if (url) {
      urlCache.set(src, url);
    }

    return url;
  } else {
    const url = getMp3Url(response.data);

    if (url) {
      urlCache.set(src, url);
    }

    return url;
  }
}

function getMp3Url(item: MediaLibraryItem): string | undefined {
  return item.Mp3Url;
}

const urls = (() => {
  switch (process.env.APP_ENV ?? process.env.NODE_ENV) {
    case "production":
      return {
        mediaLibrary: "https://medialibrary.gyldendal.dk",
      };

    case "staging":
      return {
        mediaLibrary: "https://medialibrary.gyldendal.dk",
      };

    default:
      return {
        mediaLibrary: "https://test-medialibrary.gyldendal.dk",
      };
  }
})();
